import axios from 'axios';
import { domainImageUrl, toSameOriginImageProxyUrl } from '../../utils/imageProxy';
import { ensureSignedProxyUrls } from '../../utils/signedImageProxyCache';

export async function fetchEntityImageSource(route, parentId) {
  return axios.get(`${route}/${parentId}/source`, { responseType: 'blob' });
}

/**
 * Fast path for re-editing: signed image-proxy → blob URL for Cropper.
 *
 * Why blob (not a relative `/image-proxy` URL): on AWS the SPA host and API
 * (`:5003`) are different origins. Feeding Cropper `/image-proxy?...` hits the
 * static frontend (index.html) → blank canvas. Axios uses API baseURL, so the
 * same path works for the fetch; Cropper then displays a blob: URL.
 */
export async function resolveEntityImageEditorSource(activeImage) {
  const sourceKey = activeImage?.key_source;
  if (!sourceKey) {
    return null;
  }

  await ensureSignedProxyUrls([sourceKey], { silent: true });
  // Must resolve key_source — never display_url (cropped full). See domainImageUrl size:'source'.
  const imageSrc = domainImageUrl(activeImage, { size: 'source' });
  if (!imageSrc) {
    return null;
  }

  const fetchUrl = toSameOriginImageProxyUrl(imageSrc);
  const response = await axios.get(fetchUrl, {
    responseType: 'blob',
    skipConnectivityGuard: true,
  });
  const blob = response?.data;
  if (!(blob instanceof Blob)) {
    return null;
  }
  // SPA fallback HTML mistakenly returned as 200 from the frontend host.
  if (typeof blob.type === 'string' && blob.type.includes('text/html')) {
    return null;
  }

  return { imageSrc: URL.createObjectURL(blob), originalFile: null };
}

/** Legacy fallback when only the /source API is available (no key_source). */
export async function fetchEntityImageSourceAsFile(route, parentId, entityType, entityId) {
  const response = await fetchEntityImageSource(route, parentId);
  if (response.status !== 200 && response.status !== 201) {
    return null;
  }
  const blob = response.data;
  const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
  const originalFile = new File([blob], `${entityType}_${entityId}.${ext}`, { type: blob.type });
  return {
    imageSrc: URL.createObjectURL(blob),
    originalFile,
  };
}

/**
 * Legacy dual-crop fallback: GET `{documentRoute}/{parentId}` streams type=original.
 * Used when the entity still has `*_document` rows but no active `*_image`.
 */
export async function fetchLegacyDocumentSourceAsFile(documentRoute, parentId, entityType) {
  if (!documentRoute || !parentId) {
    return null;
  }
  const response = await axios.get(`${documentRoute}/${parentId}`, { responseType: 'blob' });
  if (response.status !== 200 && response.status !== 201) {
    return null;
  }
  const blob = response.data;
  const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
  const originalFile = new File([blob], `${entityType}_${parentId}.${ext}`, { type: blob.type });
  return {
    imageSrc: URL.createObjectURL(blob),
    originalFile,
  };
}

export async function uploadEntityImage(
  config,
  parentId,
  { originalFile, croppedFile, croppedArea, aspectRatio, cropShape, rotation },
) {
  const fileData = new FormData();
  fileData.append(config.parentIdBodyKey, parentId);
  fileData.append('croppedAreaPixels', JSON.stringify(croppedArea ?? {}));
  fileData.append('aspectRatio', String(aspectRatio ?? 1));
  fileData.append('cropShape', cropShape ?? 'rect');
  fileData.append('rotation', String(rotation ?? 0));
  if (originalFile) {
    fileData.append('originalFile', originalFile);
  }
  fileData.append('croppedFile', croppedFile);
  return axios.post(config.route, fileData);
}

export async function deleteEntityImage(config, parentId) {
  return axios.delete(`${config.route}/${parentId}`);
}
